/*
 * Copyright © 2015-2021 Serenova, LLC. All rights reserved.
 */

import { createSelector } from 'reselect';
import { ednRuleToImmutable } from './ednHelpers';

import { getSkillsData } from '../skills/selectors';
import { getGroupsData } from '../groups/selectors';
import { getUsers } from '../users/selectors';
import { getTenantSlas, selectTenantSlas, getCurrentTenantDefaultSlaId } from '../slas/selectors';
import { getSelectedEntityId, getSelectedEntity, isEntityFetching, getSelectedSubEntityId } from '../selectors';
import { getCurrentFormValueByFieldName, getCurrentSubFormValueByFieldName } from '../../form/selectors';
import { List } from "immutable";

const getQueuesEntity = (state) => state.getIn(['Entities', 'queues'], new List([]));

export const getQueuesFormInitialValues = createSelector(
  [getSelectedEntityId, getSelectedEntity, getCurrentTenantDefaultSlaId],
  (selectedEntityId, selectedEntity, defaultSlaId) => {
    if (selectedEntityId === 'create') {
      return {
        basicQuery: [
          {
            map: [
              [{ key: 'after-seconds-in-queue' }, 0],
              [{ key: 'query' }, { map: [] }],
              [{ key: 'sort' }, [{ list: [{ map: [] }] }]]
            ],
          },
        ],
        activeVersion: 1,
        minPriority: 1,
        maxPriority: 1000,
        priorityValue: 1,
        priorityRate: 10,
        priorityUnit: 'seconds',
        slaId: defaultSlaId,
      };
    } else {
      return {
        name: selectedEntity.get('name'),
        id: selectedEntity.get('id'),
        description: selectedEntity.get('description'),
        activeVersion: selectedEntity.get('activeVersion'),
      };
    }
  }
);

export const currentTenantSlas = createSelector(
  [selectTenantSlas, getCurrentTenantDefaultSlaId],
  (slas, defaultSlaId) =>
    slas &&
    slas.map(
      (sla) => (sla.value === defaultSlaId ? { value: sla.value, label: `Use Tenant Default: ${sla.label}` } : sla)
    )
);

export const getQueuesPageDependentEntities = createSelector(
  [getSkillsData, getGroupsData, getUsers],
  (skills, groups, users) => ({
    skills: skills && skills.toJS(),
    groups: groups && groups.toJS(),
    users: users && users.toJS(),
  })
);

export const isAllQueuesFormDependenciesFetched = (state) =>
  getSelectedEntityId(state) === 'create'
    ? ['slas', 'tenants', 'skills', 'groups', 'users'].every((entityName) => !isEntityFetching(state, entityName))
    : ['queues', 'slas', 'skills', 'groups', 'users'].every((entityName) => !isEntityFetching(state, entityName));

export const getSelectedQueueVersionsNames = createSelector(
  [getSelectedEntity],
  (selectedEntity) =>
    selectedEntity &&
    selectedEntity.get('versions') &&
    selectedEntity
      .get('versions')
      .map((version) => ({ value: version.get('version'), label: version.get('name') }))
      .reverse() // versions should be displayed in descending order
);

//Version count could be off if client used APIs to create queue versions with non v# names
export const getVersionsCountInSelectedQueue = createSelector([getSelectedEntity], (selectedEntity) => {
  let highestVVersion = 1;
  //Protect against dup v# version from being created if client used api to create versions [CXV1-27639]
  if (selectedEntity && selectedEntity.get('versions')) {
    selectedEntity.get('versions').forEach((version) => {
      if (version.get('name').charAt(0) === 'v' && !isNaN(version.get('name').substring(1))) {
        highestVVersion =
          parseInt(version.get('name').substring(1), 10) > highestVVersion
            ? parseInt(version.get('name').substring(1), 10)
            : highestVVersion;
      }
    });
  }

  return Number(highestVVersion);
});

export const getQueueQueryFormInitialValues = createSelector(
  [getSelectedSubEntityId, getCurrentTenantDefaultSlaId],
  (selectedSubEntityId, defaultSlaId) => {
    if (selectedSubEntityId === 'create') {
      return {
        basicQuery: [
          {
            map: [
              [{ key: 'after-seconds-in-queue' }, 0],
              [{ key: 'query' }, { map: [] }],
              [{ key: 'sort' }, [{ list: [{ map: [] }] }]]
            ],
          },
        ],
        minPriority: 1,
        maxPriority: 1000,
        priorityValue: 1,
        priorityRate: 10,
        priorityUnit: 'seconds',
        slaId: defaultSlaId,
      };
    }
  }
);

export const getSelectedVersionBasicQuery = (state) =>
  getSelectedEntityId(state) === 'create'
    ? getCurrentFormValueByFieldName(state, 'basicQuery')
    : getCurrentSubFormValueByFieldName(state, `queueQuery:${getSelectedSubEntityId(state)}`, 'basicQuery');

export const getSelectedVersionQuerylevelCount = createSelector(getSelectedVersionBasicQuery, (basicQuery) => {
  return basicQuery ? basicQuery.size : 0;
});

export const getSelectedQueueVersions = createSelector(
  [getSelectedEntity, getTenantSlas],
  (selectedEntity, tenantSlas) =>
    selectedEntity &&
    selectedEntity.get('versions') &&
    selectedEntity
      .get('versions')
      .toJS()
      .map((version) => {
        // If it is older queue version, get the translatedQuery
        const basicQuery =
          version.queryVersion === 1 && version.translatedQuery
            ? ednRuleToImmutable(version.translatedQuery)
            : ednRuleToImmutable(version.query);
        const sla = tenantSlas && tenantSlas.find((sla) => sla.get('id') === version.slaId);
        return {
          ...version,
          sla: sla && sla.get('name'),
          basicQuery: basicQuery,
        };
      })
      .reverse() // versions should be displayed in descending order
);

export const getSelectedQueueActiveVersion = createSelector(getSelectedEntity, (selectedEntity) => {
  // get activeVersion only after queue versions are fetched
  return selectedEntity && selectedEntity.get('versions') && selectedEntity.getIn(['activeQueue', 'version']);
});

export const getMatchmaker2Enabled = createSelector(
  [getQueuesEntity],
  (queues) => {
    if (queues) {
      return queues.get('matchmaker2Enabled');
    } else {
      return undefined;
    }
  }
);